import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useWorkspaceStore } from '../../stores/workspaceStore'
import { salesApi, ActiveDiscount, ApiResponse, Sale } from '../../services/api'
import { workspaceService } from '../../services/workspaceService'
import { canAccessFeature, isStaleRoleRefusal } from '../../utils/permissions'
import { useWorkspaceRole } from '../../utils/useWorkspaceRole'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  CardList,
  Code,
  Eyebrow,
  ErrorState,
  Field,
  Hint,
  LoadingState,
  SettingRow,
  Stack,
  Toggle,
} from '../../ds'
import {
  discountTerms,
  discountRedemption,
  discountExpiry,
  discountKey,
  speakability,
  effectiveMentionRights,
  salesByDiscountKey,
} from './discountWording'
import type { MentionRights } from './discountWording'
import { serverSentence } from '../../utils/refusal'
import type { GrantsReadState, GrantsSnapshot } from './limitsGlance'

/**
 * Shopify codes she may bring up — the mention grant, on Limits (MAR-296, app-ia-v5 §6 Q5).
 *
 * It sits directly under the discount ceiling on purpose. Both are commercial boundaries,
 * and they are different ones: the ceiling covers what Marva may OFFER on her own, while
 * this covers whether she may RAISE a code the merchant made in Shopify. Granting is a
 * margin decision — handing out a code nobody publicised — so `POST/PUT/DELETE /sales` are
 * owner/admin on the server. Unlike the ceiling, the card is NOT behind `RestrictedTab`: a
 * member sees it READ-ONLY — every row and state exactly as an owner sees them, with the
 * switches disabled and no write controls — because what Marva may raise is something the
 * whole team should be able to read. The gate is the same `canAccessFeature(role, 'account')`
 * `RestrictedTab` uses, on the role from the SAME hook (`useWorkspaceRole`), so the ceiling and
 * this card can never disagree about who is looking at them.
 *
 * This panel used to be a form that created a sale out of facts the merchant re-typed:
 * name, percentage, scope, end date — every one of them already known to Shopify and
 * already sent to us by the `discounts/*` webhook (O1 item 3 §3.8). That is
 * reconciliation, and it drifts the moment the discount is edited in Shopify. So it is a
 * **list of what Shopify has told us is running**; each CODE discount carries one control
 * — may Marva bring this up, and in whose words. Automatic discounts need no permission
 * and unspeakable ones cannot be given one, so both collapse into a single summary line.
 *
 * Nothing here sends anything — a code she may raise reaches a shopper only inside a moment
 * already happening for its own reasons. Compare-at markdowns (was/now prices) get no row:
 * they are per-item ground truth, always speakable, and arrive as `products/update`.
 */

/**
 * Who is reading a sentence about the grant: someone who may change it (`editor`, an owner or
 * admin), or someone who may only read it (`reader`, a member).
 */
type Viewer = 'editor' | 'reader'

/**
 * The ONE summary of the merchant's words for a grant — the owner's editor button and the
 * member's read-only line both render this, so the two views cannot drift (PR #237 review).
 *
 * The two fields are different facts and are never folded into one: `name` is what the store
 * calls it, `discountDisplay` is how Marva should describe it. Presenting a bare name as her
 * wording would claim a sentence nobody wrote. `null` when neither is set.
 *
 * ⚠️ **Whose words depends on who is reading** (PR #237 round 3). To an owner or admin they are
 * "your words"; a member did not write them and cannot change them, so to a member they are the
 * store's — "Your words: you call it …" would attribute an admin's wording to the person reading
 * it. Only the attribution changes; the facts and their order are the same for both.
 */
const wordingSummary = (sale: Pick<Sale, 'name' | 'discountDisplay'>, viewer: Viewer): string | null => {
  const name = (sale.name || '').trim()
  const display = (sale.discountDisplay || '').trim()
  const clauses = [
    name ? (viewer === 'editor' ? `you call it “${name}”` : `called “${name}”`) : null,
    display ? `she describes it as “${display}”` : null,
  ].filter(Boolean)
  if (clauses.length === 0) return null
  return `${viewer === 'editor' ? 'Your words' : 'The store’s words'}: ${clauses.join('; ')}`
}

/**
 * The merchant's own words for a discount Marva may raise. Optional by design: with
 * nothing here she describes it from Shopify's own terms, which is already truthful.
 */
const MerchantWording = ({
  sale,
  workspaceId,
  generationRef,
  onSaved,
}: {
  sale: Sale
  workspaceId: string
  // The panel's staleness token, read in the save handler (never during render). A PUT
  // resolves after an await like any other mutation, so its landing needs the same guard
  // every other one here has.
  generationRef: { current: number }
  // `failed` is decided by the response, never by reading the message: a refusal worded
  // without "could not" (a 403 role refusal, a 404, a 400) is still a refusal.
  onSaved: (message: string, generation: number, outcome: { failed: boolean }) => void
}) => {
  const [open, setOpen] = useState(false)
  const [name, setName] = useState(sale.name || '')
  const [discountDisplay, setDiscountDisplay] = useState(sale.discountDisplay || '')
  const [isSaving, setIsSaving] = useState(false)
  const summary = wordingSummary(sale, 'editor')

  if (!open) {
    return (
      <div>
        <Button variant="link" onClick={() => setOpen(true)}>
          {summary ? `${summary}, edit` : 'Your words for it'}
        </Button>
      </div>
    )
  }

  const save = async () => {
    const generation = generationRef.current
    const asked = issuedAs()
    setIsSaving(true)
    try {
      // `null`, not `undefined`, when cleared: JSON drops undefined and the handler only
      // writes a field it can see, so clearing both would otherwise 400 as "no fields to
      // update" and silently leave the old wording standing.
      const response = await salesApi.update(workspaceId, sale.saleId, {
        name: name.trim() || null,
        discountDisplay: discountDisplay.trim() || null,
      })
      const refusal = response.success ? null : refusalMessage(response, 'Could not save your wording', asked)
      if (generation !== generationRef.current) return
      if (response.success) {
        onSaved('Saved your words for it.', generation, { failed: false })
        setOpen(false)
      } else {
        onSaved(refusal as string, generation, { failed: true })
      }
    } catch {
      if (generation !== generationRef.current) return
      onSaved('Could not save your wording', generation, { failed: true })
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <CardList>
      <Field
        id={`words-name-${sale.saleId}`}
        label="What you call it"
        value={name}
        onChange={(e) => setName((e.target as HTMLInputElement).value)}
        placeholder="Summer clearance"
      />
      <Field
        id={`words-display-${sale.saleId}`}
        label="How she should describe it"
        value={discountDisplay}
        onChange={(e) => setDiscountDisplay((e.target as HTMLInputElement).value)}
        placeholder="Up to 30% off"
        hint="Optional. Leave both empty and she describes it in Shopify's own terms, which are already true."
      />
      <CardFooter>
        <Button size="sm" onClick={save} busy={isSaving} busyLabel="Saving…">
          Save wording
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
          Cancel
        </Button>
      </CardFooter>
    </CardList>
  )
}

/**
 * A read either landed, or it did not — and "did not" is not a synonym for "empty".
 * `apiClient` resolves both HTTP errors and network failures as `{ success: false }`
 * rather than throwing, so a panel that only catches thrown errors keeps its initial
 * empty arrays and renders a failed read as an established negative: "Shopify has told
 * us nothing is running", every mention right off. Both are claims, and both are false.
 */
type ReadState = GrantsReadState

/**
 * Everything this panel knows, and the store it knows it about.
 *
 * The workspace id is not metadata here — it is the first field, and every read of the
 * rest goes through a comparison against the currently selected store. State that is
 * merely *cleared* on a switch is state that can still be painted for one render under
 * the wrong store's name, because a selection changes during render and an effect runs
 * after it. Stamping makes that paint unrepresentable rather than short: rows belonging
 * to another workspace are not "stale data to be cleaned up", they are not this panel's
 * data at all, and the derivation below simply cannot reach them.
 */
type PanelData = GrantsSnapshot

const NO_DISCOUNTS: ActiveDiscount[] = []
const NO_SALES: Sale[] = []

/** What a hand-typed legacy row is called, for its line and its Remove button. */
const legacyLabel = (sale: Sale) => sale.name || sale.discountDisplay || 'an untitled entry'

/** The row's wording, when it says something the label does not. */
const legacyWording = (sale: Sale) =>
  sale.discountDisplay && sale.discountDisplay !== legacyLabel(sale) ? sale.discountDisplay : null

const addedOn = (iso: string | undefined): string | null => {
  if (!iso) return null
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return null
  return at.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

/** How much of a canonical-UUID `saleId` names a legacy row — see `legacyRef`. */
const REF_LENGTH = 6
const CANONICAL_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * The row's own reference, for its Remove button's accessible name: the last 6 characters of a
 * canonical UUID `saleId` (random, so six hex characters are 16.7M values and a collision among
 * one store's handful of hand-typed rows is not a realistic case), otherwise the WHOLE id — a
 * `saleId` that is not a random UUID (a discount id, a numeric Shopify gid) can share any tail
 * with another, and only the full id is certain to differ. A `SALE#` key prefix is stripped.
 */
const legacyRef = (saleId: string): string => {
  const id = saleId.startsWith('SALE#') ? saleId.slice('SALE#'.length) : saleId
  return CANONICAL_UUID.test(id) ? id.slice(-REF_LENGTH) : id
}

/**
 * A legacy row's visible "added …" note and its Remove button's accessible name (PR #237).
 *
 * Remove cannot be undone, so two rows that render "Summer sale · Not spoken · Remove" with
 * one shared accessible name are a delete the merchant cannot aim. The name therefore carries
 * everything the row says — its label, its wording when different, its code — plus when it was
 * typed in ("added {day}", when `createdAt` is a usable date) and, ALWAYS, `ref {legacyRef}`.
 * The visible note is only "added {day}" (nothing when there is no usable date): round 5 found
 * the ref on every row was noise for the stores with no look-alikes at all, so it lives in the
 * accessible name, where the irreversible act needs it.
 *
 * ⚠️ **A pure function of the row — nothing about its neighbours is consulted.** Earlier rounds
 * added the date or reference only to rows that collided, and widened a tail to the full id when
 * two tails matched; both looked at the group, so removing one row changed the others' labels —
 * the irreversible button's name moving under the merchant (PR #237 rounds 3 and 4). Whether a
 * ref is a 6-char tail or the full id is decided by the row's own `saleId` shape (`legacyRef`),
 * never by whether another row shares its tail.
 */
const legacyIdentity = (sale: Sale): { added: string | null; removeLabel: string } => {
  const wording = legacyWording(sale)
  const base = `“${legacyLabel(sale)}”${wording ? ` — ${wording}` : ''}${sale.discountCode ? ` (${sale.discountCode})` : ''}`
  const day = addedOn(sale.createdAt)
  return {
    added: day ? `added ${day}` : null,
    removeLabel: `Remove ${base}, ${day ? `added ${day}, ` : ''}ref ${legacyRef(sale.saleId)}`,
  }
}

/** Who a write was issued as, captured BEFORE it is sent — see `refusalMessage`. */
type IssuedAs = { clerkUserId: string | undefined }
const issuedAs = (): IssuedAs => ({
  clerkUserId: useWorkspaceStore.getState().userMembership?.clerkUserId,
})

/**
 * The sentence for a write the server did not accept — and, when the refusal says the caller's
 * role here is not what this card believed, a membership refresh.
 *
 * ⚠️ **In the server's words.** The service words its refusals for the merchant (the 503
 * `role_check_unavailable` says "…so nothing was changed. Try again.", which is the part that
 * tells her the grant did not land). Rewording it here dropped that clause and left the
 * refusal written in two places, so `message`, then `error`, then the local fallback — and
 * nothing branches on `details.reason`, because there is nothing the server leaves unworded.
 * `apiClient` carries the body's `message` on BOTH failure shapes — an HTTP error and a 200
 * with `success: false` — so the sentence survives whichever status the service answered with
 * (PR #237 round 3: the 200 shape used to keep only `data.error`).
 *
 * ⚠️ **A stale-role refusal locks the card without a reload.** A 403
 * `mention_grant_requires_admin` carries `details.roleReason`; when that says she was removed
 * or demoted (`isStaleRoleRefusal`) the membership is re-read through the one path TeamView
 * and CustomerErasure use, and once that read lands `useWorkspaceRole` turns the switches
 * read-only. The refusal deletes nothing itself — a `no_workspace_membership` straight after
 * creating the workspace is what a lagging index answers, so the re-read decides, not the
 * refusal (PR #237 round 7). Done before any staleness check: the refusal is about the
 * workspace and user that ISSUED the write, whatever is on screen now.
 */
const refusalMessage = (response: ApiResponse<unknown>, fallback: string, asked: IssuedAs): string => {
  // The identity check is the point — a refusal that outlived a sign-out or a user switch (or
  // whose issuer is unknown) says nothing about whoever holds the store now.
  if (isStaleRoleRefusal(response)) {
    void workspaceService.refreshMembership({ refusedClerkUserId: asked.clerkUserId })
  }
  return serverSentence(response, fallback)
}

/** The three kinds, each discount classified ONCE by the one verdict `speakability` gives. */
type Classified = {
  code: ActiveDiscount[]
  automatic: ActiveDiscount[]
  silent: { d: ActiveDiscount; reason: string }[]
}
const classifyDiscounts = (discounts: ActiveDiscount[]): Classified => {
  const out: Classified = { code: [], automatic: [], silent: [] }
  for (const d of discounts) {
    const verdict = speakability(d)
    if (verdict.kind === 'code') out.code.push(d)
    else if (verdict.kind === 'automatic') out.automatic.push(d)
    else out.silent.push({ d, reason: verdict.reason })
  }
  return out
}

interface MentionGrantsSectionProps {
  /**
   * Every settled read of this card's data (MAR-385), so the Limits "At a glance" aside counts
   * the codes she may raise from the SAME rows this card shows, including after a grant or a
   * revoke here. Never called while a read is pending.
   */
  onRead?: (snapshot: GrantsSnapshot) => void
}

const MentionGrantsSection = ({ onRead }: MentionGrantsSectionProps = {}) => {
  // The same presentation gate `RestrictedTab` applies, on the role from the SAME hook, derived
  // at render — never copied into state, so a role that loads or changes is reflected in the
  // very next paint. The server is the authorization control (owner/admin on every write); this
  // only avoids offering a member a control it will refuse. See `useWorkspaceRole` for why it
  // is asked about THIS workspace and never read off `currentUser.role`.
  const { workspaceId, role } = useWorkspaceRole()
  const canWrite = canAccessFeature(role, 'account')

  const [data, setData] = useState<PanelData | null>(null)
  // The merchant's own in-flight marker and the last sentence we showed her are just as
  // much this-store facts as the rows are — "Marva may now bring up X" under a store
  // where nothing was granted is a false receipt — so they carry the same stamp.
  const [notice, setNotice] = useState<{ workspaceId: string; text: string; isError: boolean } | null>(null)
  const [pending, setPending] = useState<{ workspaceId: string; key: string } | null>(null)
  // Which store the "Show why" list is open for. Only a UI fact, but stamped like the rest:
  // a disclosure opened under one store is not an answer the next one asked for.
  const [whyOpenFor, setWhyOpenFor] = useState<string | null>(null)

  // ⚠️ The single gate. Nothing below reads `data`, `notice` or `pending` directly.
  const current = data && data.workspaceId === workspaceId ? data : null
  const discounts = current?.discounts ?? NO_DISCOUNTS
  const sales = current?.sales ?? NO_SALES
  const discountsRead: ReadState = current?.discountsRead ?? 'pending'
  const salesRead: ReadState = current?.salesRead ?? 'pending'
  const discountsTruncated = current?.discountsTruncated ?? false
  const salesTruncated = current?.salesTruncated ?? false
  const ownNotice = notice && notice.workspaceId === workspaceId ? notice : null
  const message = ownNotice?.text ?? null
  const isError = ownNotice?.isError ?? false
  const pendingKey = pending && pending.workspaceId === workspaceId ? pending.key : null
  const showWhy = whyOpenFor !== null && whyOpenFor === workspaceId

  /**
   * The token EVERY asynchronous state write in this panel is stamped with.
   *
   * ⚠️ Not just the initial load. The guard originally covered `load()` alone, and the
   * hole was the post-mutation refresh: grant/revoke/remove each await a write and then
   * call `loadSales()`, so a merchant who switched store while a grant was in flight got
   * the NEW workspace's `load()` first and the OLD workspace's sales landing on top of
   * it — the previous merchant's legacy rows and mention rights, rendered against this
   * merchant's discounts. Showing one merchant another merchant's grants is the worst
   * available version of asserting something we cannot back.
   *
   * So the rule is mechanical: capture the generation before the first `await`, and
   * write nothing after any `await` unless it is still current.
   */
  const loadGeneration = useRef(0)

  const isLoading = discountsRead === 'pending' || salesRead === 'pending'

  // Report each settled read upward. A parent should pass a stable callback (LimitsTab's is a
  // `useCallback`); an unstable one only re-reports the same rows, which is harmless.
  useEffect(() => {
    if (current && current.discountsRead !== 'pending' && current.salesRead !== 'pending') onRead?.(current)
  }, [current, onRead])
  // Mention rights are only knowable if the sales read landed. Until it does, a toggle
  // would render "off" — which reads as "you have granted nothing" — and acting on it
  // would POST a second grant onto a discount that already had one.
  //
  // ⚠️ This says the read LANDED, not that it was whole. Per-discount rights go through
  // `mentionRights`, which also weighs `salesTruncated`; this one governs only the two
  // whole-list statements — the older-entries list, and "we could not read your sales".
  const rightsKnown = salesRead === 'ok'
  const salesReadState = { landed: salesRead === 'ok', complete: !salesTruncated }
  // One clock reading for the whole render, so a control and the sentence beside it
  // cannot disagree about whether a grant has started yet.
  const nowIso = new Date().toISOString()

  const note = (text: string, failed = false) => {
    if (!workspaceId) return
    setNotice({ workspaceId, text, isError: failed })
  }

  /**
   * Re-read the mention rights after a mutation. `generation` defaults to the current
   * token so a bare call is still safe, but a caller that already awaited something must
   * pass ITS token — the one taken before its own first `await` — or the guard checks a
   * generation that a workspace switch has already moved past.
   */
  const loadSales = useCallback(async (generation: number = loadGeneration.current) => {
    if (!workspaceId) return
    let landed: Sale[] | null = null
    let landedTruncated = false
    try {
      const response = await salesApi.list(workspaceId)
      if (response.success && response.data) {
        landed = response.data.sales || []
        landedTruncated = response.data.truncated === true
      }
    } catch {
      // fall through — an unsuccessful response and a thrown one mean the same thing here
    }
    if (generation !== loadGeneration.current) return
    const rows = landed
    // The functional form is the second lock, behind the generation token: a refresh may
    // only ever amend the data object belonging to the store it read from.
    setData((prev) => {
      if (!prev || prev.workspaceId !== workspaceId) return prev
      if (rows) return { ...prev, sales: rows, salesTruncated: landedTruncated, salesRead: 'ok' }
      // A refresh we could not complete leaves the rows describing a state we no longer
      // know to be current, so say so rather than letting them stand as fact.
      return { ...prev, salesTruncated: false, salesRead: 'failed' }
    })
  }, [workspaceId])

  const load = useCallback(async () => {
    if (!workspaceId) return
    const generation = ++loadGeneration.current
    const empty: PanelData = {
      workspaceId,
      discounts: [],
      sales: [],
      discountsRead: 'pending',
      salesRead: 'pending',
      discountsTruncated: false,
      salesTruncated: false,
    }
    setData(empty)
    try {
      const [discountsRes, salesRes] = await Promise.all([
        salesApi.listDiscounts(workspaceId),
        salesApi.list(workspaceId),
      ])
      if (generation !== loadGeneration.current) return
      // An unsuccessful response is a failed read, not an empty store. The read stopped
      // at its row cap: what came back is SHORT, not complete — and a list that is
      // quietly short is the same failure as an empty list we never received.
      setData({
        ...empty,
        ...(discountsRes.success && discountsRes.data
          ? {
              discounts: discountsRes.data.discounts || [],
              discountsTruncated: discountsRes.data.truncated === true,
              discountsRead: 'ok' as const,
            }
          : { discountsRead: 'failed' as const }),
        ...(salesRes.success && salesRes.data
          ? {
              sales: salesRes.data.sales || [],
              salesTruncated: salesRes.data.truncated === true,
              salesRead: 'ok' as const,
            }
          : { salesRead: 'failed' as const }),
      })
      const failure = !discountsRes.success ? discountsRes.error : !salesRes.success ? salesRes.error : null
      if (!discountsRes.success || !salesRes.success) {
        note(failure || 'Could not check your sales and discounts just now.', true)
      }
    } catch (err) {
      if (generation !== loadGeneration.current) return
      setData({ ...empty, discountsRead: 'failed', salesRead: 'failed' })
      note(err instanceof Error ? err.message : 'Could not check your sales and discounts just now.', true)
    }
  }, [workspaceId])

  useEffect(() => {
    // Invalidate BEFORE this workspace's own reads start, and independently of `load`,
    // which returns early (without bumping) when there is no workspace at all. After
    // this line every request already in flight — a read, a grant, a wording save, the
    // refresh that follows one — can no longer write anything.
    loadGeneration.current += 1
    // Belt to the stamp's braces: the stamp already makes the previous store's marker and
    // message unreadable from here, but they would become readable again if the merchant
    // switched back, and a receipt from before she left is not a receipt for now.
    setPending(null)
    setNotice(null)
    load()
  }, [load])

  // Annotations, GROUPED by the discount's own id — see discountKey(). Every row `GET
  // /sales` returned, INCLUDING ended and not-yet-started ones: the endpoint filters
  // nothing, so whether a row is actually a live grant is `effectiveGrant`'s question,
  // not this map's. Keying only the live ones here would lose the scheduled row the
  // panel has to show her, and the ended row she can re-grant.
  //
  // ⚠️ A group, not a row. This used to be a `Map<string, Sale>` filled by `set()` per
  // row, so a discount with both an ended and an active annotation resolved to whichever
  // the API listed last — see `effectiveGrant` for what that cost the merchant.
  const salesByDiscount = salesByDiscountKey(sales)
  // Pre-phase-5 rows: hand-typed announcements with no link to a detected discount.
  // Nothing writes these any more — `applyMentionRights` joins on `linkedPriceRuleId`,
  // so a row without one never pairs, never reaches `speakable`, and is never rendered
  // to the agent. They are shown ONLY so a merchant can see and clear them; showing
  // them as if they were live mention rights would be the lie this phase removes.
  const legacySales = sales.filter((s) => !s.linkedPriceRuleId)

  const grant = async (d: ActiveDiscount) => {
    if (!workspaceId) return
    const generation = loadGeneration.current
    const asked = issuedAs()
    const key = discountKey(d)
    setPending({ workspaceId, key })
    try {
      // ONLY the link. Value, scope, entitlements, dates — and the redemption code —
      // are Shopify's facts, which the handler reads off the hydrated discount row
      // itself (`applyMentionRights` prefers `d.code` over anything stored here).
      // Echoing the code back would put a second copy of it on `active-sales`, and a
      // second copy drifts the moment the merchant rotates the code: exactly the
      // reconciliation §3.8 exists to delete, and a contradiction of this panel's own
      // promise that you do not re-enter any of it. The merchant types nothing.
      const response = await salesApi.create(workspaceId, {
        linkedPriceRuleId: key,
      })
      const refusal = response.success ? null : refusalMessage(response, 'Could not save that', asked)
      if (generation !== loadGeneration.current) return
      if (refusal !== null) throw new Error(refusal)
      await loadSales(generation)
      if (generation !== loadGeneration.current) return
      note(`Marva may now bring up “${d.title}”.`)
    } catch (err) {
      if (generation !== loadGeneration.current) return
      note(err instanceof Error ? err.message : 'Could not save that', true)
    } finally {
      // Not the new workspace's pending key: this mutation never belonged to it.
      if (generation === loadGeneration.current) setPending(null)
    }
  }

  const revoke = async (d: ActiveDiscount, sale: Sale) => {
    if (!workspaceId) return
    const generation = loadGeneration.current
    const asked = issuedAs()
    const key = discountKey(d)
    setPending({ workspaceId, key })
    try {
      const response = await salesApi.delete(workspaceId, sale.saleId)
      const refusal = response.success ? null : refusalMessage(response, 'Could not save that', asked)
      if (generation !== loadGeneration.current) return
      if (refusal !== null) throw new Error(refusal)
      await loadSales(generation)
      if (generation !== loadGeneration.current) return
      note(`Marva will keep quiet about “${d.title}”. It still runs in your store.`)
    } catch (err) {
      if (generation !== loadGeneration.current) return
      note(err instanceof Error ? err.message : 'Could not save that', true)
    } finally {
      if (generation === loadGeneration.current) setPending(null)
    }
  }

  const removeLegacy = async (sale: Sale) => {
    if (!workspaceId) return
    const generation = loadGeneration.current
    const asked = issuedAs()
    setPending({ workspaceId, key: sale.saleId })
    try {
      const response = await salesApi.delete(workspaceId, sale.saleId)
      const refusal = response.success ? null : refusalMessage(response, 'Could not remove that', asked)
      if (generation !== loadGeneration.current) return
      if (refusal !== null) throw new Error(refusal)
      await loadSales(generation)
      if (generation !== loadGeneration.current) return
      note(`Removed “${legacyLabel(sale)}”.`)
    } catch (err) {
      if (generation !== loadGeneration.current) return
      note(err instanceof Error ? err.message : 'Could not remove that', true)
    } finally {
      if (generation === loadGeneration.current) setPending(null)
    }
  }

  // The resolved grant is computed ONCE per discount, in `renderCodeDiscount`, and handed
  // down. Recomputing it here would be a second place for the verdict to be decided, and
  // a control that disagreed with the sentence beside it is the defect this reduction
  // exists to remove. Only CODE discounts reach this — automatic and unspeakable ones have
  // no control at all and live in the collapsed list below.
  const renderControl = (d: ActiveDiscount, standing: MentionRights, sale: Sale | undefined) => {
    const key = discountKey(d)

    // Unknown is not off. Rendering the toggle here would state that no mention right
    // exists, and flipping it would grant a second one on top of the first. `unknown`
    // now covers two reads, not one: the read that never landed, and the read that
    // stopped at its cap without this discount's grant in it. The second used to render
    // as an off switch with no `saleId` behind it, so it could be granted again and
    // again and never revoked — the panel insisting she had allowed nothing while
    // refusing her the control that would take the permission back.
    if (standing.kind === 'unknown') {
      return <Badge>Not checked</Badge>
    }
    // A grant dated forward is neither on nor off, and both of those would misstate it:
    // "on" claims a right that is not yet in force, "off" denies a decision she made.
    // So it is named, and it stays withdrawable.
    if (standing.kind === 'scheduled' && sale) {
      return (
        <CardList>
          <Badge tone="gold">Scheduled</Badge>
          {canWrite && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => revoke(d, sale)}
              disabled={pendingKey === key}
              aria-label={`Cancel the scheduled permission for “${d.title}”`}
            >
              Cancel
            </Button>
          )}
        </CardList>
      )
    }
    return (
      <div>
        <div className="t-hint" aria-hidden="true">May she bring it up?</div>
        <Toggle
          // A row exists ≠ a right is in force. `grantStanding` is the reader's own rule,
          // so an ended annotation reads off — and flipping it on re-grants, which is
          // exactly what `POST /sales` does with an ended row.
          checked={standing.kind === 'active'}
          label={`May Marva bring up ${d.title}?`}
          labelHidden
          // Stays in the tab order whether read-only, busy or not (the DS switch is a <button>).
          tabIndex={0}
          // Read-only for a member: the switch still states the truth (`aria-checked`) and
          // stays in the tab order so a keyboard or screen-reader user can hear it — it just
          // cannot be flipped. In flight it is `busy`, never `disabled`: `disabled` leaves the
          // tab order, so an owner who pressed Space would lose focus to the page mid-write.
          readOnly={!canWrite}
          busy={canWrite && pendingKey === key}
          onChange={() => (standing.kind === 'active' && sale ? revoke(d, sale) : grant(d))}
        />
      </div>
    )
  }

  const renderCodeDiscount = (d: ActiveDiscount) => {
    const key = discountKey(d)
    // One verdict per discount, reduced from every annotation against it by standing —
    // not by whichever row the API happened to list last. `sale` is the row that verdict
    // came from, so it is the row the revoke and the wording editor act on.
    const { rights: standing, sale } = effectiveMentionRights(
      salesByDiscount.get(key),
      salesReadState,
      nowIso
    )

    return (
      <Card padding="sm" key={key} data-testid={`discount-${key}`}>
        <CardHeader
          title={
            <>
              {d.title} {d.code && <Code>{d.code}</Code>}
            </>
          }
          description={`${discountTerms(d)} · ${discountExpiry(d)}`}
          aside={renderControl(d, standing, sale)}
        />
        <CardBody>
          {standing.kind === 'unknown' && (
            // Two different unknowns, and they are not interchangeable: one is "we could
            // not ask", the other is "we asked and the answer was cut short before this
            // row". Saying the first when the second is true would misdescribe a working
            // store as a broken connection.
            <Hint>
              {/* A member did not decide this and cannot, so she is not told "you". */}
              {rightsKnown ? (
                <>
                  This one was not in the part of your sales we managed to read, so we cannot say whether{' '}
                  {canWrite ? 'you have' : 'an owner or admin has'} already allowed it — it is shown neither on nor
                  off. Nothing has changed in your store.
                </>
              ) : (
                <>
                  We could not check whether {canWrite ? 'you have' : 'an owner or admin has'} already allowed Marva
                  to raise this one, so it is shown neither on nor off. Nothing has changed in your store, and she
                  keeps to whatever {canWrite ? 'you last decided' : 'was last decided'}.
                </>
              )}
            </Hint>
          )}
          {standing.kind === 'scheduled' && (
            // A member cannot cancel, so she is not told to.
            <Hint>
              {canWrite ? (
                <>
                  You have allowed Marva to raise this from {new Date(standing.startsAt).toLocaleDateString()}. She
                  keeps quiet about it until then — cancel and she never raises it at all.
                </>
              ) : (
                <>
                  Marva may raise this from {new Date(standing.startsAt).toLocaleDateString()}. She keeps quiet about
                  it until then.
                </>
              )}
            </Hint>
          )}
          {(standing.kind === 'active' || standing.kind === 'scheduled') &&
            sale &&
            workspaceId &&
            (canWrite ? (
              <MerchantWording
                sale={sale}
                workspaceId={workspaceId}
                generationRef={loadGeneration}
                onSaved={(m, generation, { failed }) => {
                  note(m, failed)
                  loadSales(generation)
                }}
              />
            ) : (
              wordingSummary(sale, 'reader') && <Hint>{wordingSummary(sale, 'reader')}</Hint>
            ))}
          {standing.kind === 'none' && (
            // Covers both "you never granted this" and "the grant you made has ended":
            // in either case no right stands, and the sentence is true of both.
            // A member cannot say she may, so the sentence names who can.
            <Hint>
              {canWrite
                ? 'Handing out a code that was not publicised is a margin decision, so she will not raise it unless you say she may.'
                : 'Handing out a code that was not publicised is a margin decision, so she will not raise it unless an owner or admin allows it.'}
            </Hint>
          )}
        </CardBody>
      </Card>
    )
  }

  // An automatic or unspeakable discount: no control, only the reason there is none. The
  // verdict was reached once, in `classifyDiscounts`; `silentReason` is its reason, or
  // undefined for an automatic one.
  const renderOtherDiscount = (d: ActiveDiscount, silentReason?: string) => {
    const automatic = silentReason === undefined
    const key = discountKey(d)
    return (
      <Card padding="sm" tone="recessed" key={key} data-testid={`discount-${key}`}>
        <CardHeader
          title={d.title}
          description={`${discountTerms(d)} · ${discountRedemption(d)} · ${discountExpiry(d)}`}
          aside={automatic ? <Badge tone="sage">Always speakable</Badge> : <Badge>Not speakable</Badge>}
        />
        <CardBody>
          {automatic && (
            // NOT "it applies to everyone" — an automatic discount can be scoped to
            // products or a collection, or sit behind a minimum spend, and we have not
            // checked which. What we do know is the part that makes the toggle
            // unnecessary: no code changes hands, so there is no margin decision to take.
            <Hint>
              Shopify applies it at checkout for whoever qualifies, so there is nothing to give away — no code changes
              hands. Marva can say so whenever it is relevant; staying quiet would only mean a shopper understates
              what they would pay.
            </Hint>
          )}
          {!automatic && <Hint>{silentReason}</Hint>}
        </CardBody>
      </Card>
    )
  }

  // The three kinds, from the ONE verdict `speakability` gives per discount, in one pass.
  // Code discounts are what this card is for; the other two are summarised, never given a switch.
  const {
    code: codeDiscounts,
    automatic: automaticDiscounts,
    silent: silentDiscounts,
  } = useMemo(() => classifyDiscounts(discounts), [discounts])
  const automaticCount = automaticDiscounts.length
  const silentCount = silentDiscounts.length
  const summaryClauses = [
    automaticCount > 0
      ? automaticCount === 1
        ? '1 applies automatically, so she can mention it whenever it is relevant'
        : `${automaticCount} apply automatically, so she can mention them whenever they are relevant`
      : null,
    silentCount > 0 ? `${silentCount} she cannot describe` : null,
  ].filter(Boolean)

  const legacyCount = legacySales.length

  return (
    <Card data-testid="mention-grants">
      <CardHeader
        title="Shopify codes she may bring up"
        titleAs="h2"
        description={
          <>
            {canWrite ? 'Codes you made in Shopify' : 'Codes made in this store’s Shopify'} that are live right
            now. Separate from the ceiling in Discounts, which covers what she
            offers on her own. Nothing here sends anything — a code she may raise reaches someone only inside a moment
            that was already happening for its own reasons.
          </>
        }
      />

      <CardList>
        {!canWrite && (
          <Hint data-testid="mention-grants-read-only">
            Only an owner or admin can change what Marva may bring up.
          </Hint>
        )}

        {message && (
          <Alert tone={isError ? 'danger' : 'success'} live="polite">
            {message}
          </Alert>
        )}

        <Eyebrow tone="muted">From Shopify</Eyebrow>

        {isLoading ? (
          <LoadingState caption="Reading your discounts…" />
        ) : discountsRead === 'failed' ? (
          // "We could not check" is a different sentence from "there is nothing", and the
          // difference is the whole point of this panel. An empty list we never received
          // must never render as Shopify's answer.
          <ErrorState title="We could not check your Shopify discounts just now." onRetry={() => load()} retryLabel="Try again">
            This list is not showing what is running — it is showing nothing at all. That is our problem, not a
            statement about your store.
          </ErrorState>
        ) : (
          <>
            {/* A capped read is "we could not show you everything", which is a different
                sentence from "there is nothing" — the same distinction the failed read
                above turns on. Dropping the flag collapses the first into the second, and
                a discount missing from this list has no control here either. */}
            {discountsTruncated && (
              <Alert tone="attention">
                This is not the whole list. Your store has more discounts than we can read in one go, so some that are
                running are missing from it — and one that is missing has no control here. What is shown is accurate;
                what is not shown is our limit, not a statement about your store.
              </Alert>
            )}
            {salesRead === 'ok' && salesTruncated && discounts.length > 0 && (
              // A grant that fell off the end of a capped read used to render as a toggle
              // in the OFF position, and off is a claim: "you have allowed nothing here".
              // Worse, it was an ACTIONABLE claim with no way back — the switch offered a
              // grant and could never offer the revoke, because the row carrying the
              // saleId was the very thing the cap dropped. Those rows are now named as
              // unchecked instead, and this says which way the panel erred.
              <Alert tone="attention">
                We could not read all of what {canWrite ? 'you have' : 'an owner or admin has'} already allowed. Any code
                we could not confirm is marked <strong>Not checked</strong> rather than given a switch, because a switch
                would have to claim either that {canWrite ? 'you allowed it or that you did not' : 'it was allowed or that it was not'}.
              </Alert>
            )}
            {discounts.length === 0 && !discountsTruncated ? (
              <Card tone="recessed" padding="sm">
                <p className="t-small">
                  Shopify has not told us about any discount running now. If one is running through an app or a Shopify
                  Function we cannot see it — but its marked-down prices still reach shoppers, as the was/now price on
                  each item. There is nothing for you to do here.
                </p>
              </Card>
            ) : (
              <>
                {codeDiscounts.map(renderCodeDiscount)}
                {summaryClauses.length > 0 && (
                  <>
                    <div>
                      <Button
                        variant="ghost"
                        size="sm"
                        wrap
                        aria-expanded={showWhy}
                        onClick={() => setWhyOpenFor(showWhy ? null : workspaceId ?? null)}
                        iconRight={<span>{showWhy ? 'Hide' : 'Show why'}</span>}
                      >
                        {summaryClauses.join(' · ')}
                      </Button>
                    </div>
                    {showWhy && (
                      <CardList>
                        {automaticDiscounts.map((d) => renderOtherDiscount(d))}
                        {silentDiscounts.map(({ d, reason }) => renderOtherDiscount(d, reason))}
                        {automaticCount > 0 && (
                          <Hint>
                            Automatic discounts need no permission: no code changes hands, so there is nothing to give
                            away.
                          </Hint>
                        )}
                      </CardList>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}

        {/* Pre-phase-5 rows, typed in by hand. Shown ONLY so a merchant can clear them —
            `applyMentionRights` joins on `linkedPriceRuleId`, so she never raises them. */}
        {!isLoading && !rightsKnown && (
          <Alert tone="attention">
            We could not read your sales just now, so we cannot say whether any older entries are still here.
          </Alert>
        )}

        {rightsKnown && salesTruncated && (
          <Alert tone="attention">
            We could not read all of your sales, so this list may be short of older entries typed in by hand. What is
            shown is real; what is missing is our limit, not a statement about your store.
          </Alert>
        )}

        {rightsKnown && legacyCount > 0 && (
          <Stack gap="sm" data-testid="legacy-entries">
            <p className="t-small">
              {legacyCount} older {legacyCount === 1 ? 'entry' : 'entries'} typed in by hand before Marva read your
              discounts from Shopify. She does not raise {legacyCount === 1 ? 'it' : 'them'}.
              {/* Remove is an owner/admin act; a member is told who can, not shown a promise. */}
              {!canWrite && ` Only an owner or admin can remove ${legacyCount === 1 ? 'it' : 'them'}.`}
            </p>
            {legacySales.map((legacy) => (
              // Enough to tell rows apart and to know what each one is: two entries can share
              // a name, so the wording, code and when it was added show, and the Remove button's
              // accessible name carries all of it plus the row's own reference — each derived
              // from this row alone (`legacyIdentity`).
              <SettingRow
                key={legacy.saleId}
                data-testid={`legacy-${legacy.saleId}`}
                title={legacyLabel(legacy)}
                description={
                  <>
                    {legacyWording(legacy) && <span>{legacyWording(legacy)} </span>}
                    {legacy.discountCode && <Code>{legacy.discountCode}</Code>}
                    {legacyIdentity(legacy).added && <span> {legacyIdentity(legacy).added}</span>}
                  </>
                }
                aside={
                  <>
                    <Badge>Not spoken</Badge>
                      {canWrite && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeLegacy(legacy)}
                          disabled={pendingKey === legacy.saleId}
                          aria-label={legacyIdentity(legacy).removeLabel}
                        >
                        Remove
                      </Button>
                    )}
                  </>
                }
              />
            ))}
          </Stack>
        )}
      </CardList>
    </Card>
  )
}

export default MentionGrantsSection
